import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Phone, MessageSquare, MailOpen, RefreshCw, Bell, BellOff, ChevronDown, ChevronUp, Play, Wrench, ShieldAlert, PhoneForwarded, Sparkles, TrendingUp, Mic } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Tabs, TabsList, TabsTrigger,
} from '../../components/ui/tabs';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import useInboxUnread from '../../hooks/useInboxUnread';
import { listInteractions, markInteractionRead } from './crmApi';
import ValuationDetailDialog from './ValuationDetailDialog';

function fmt(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); }
  catch { return iso; }
}

function InteractionCard({ item, onOpen, onValuationClick }) {
  const [expanded, setExpanded] = useState(false);
  const isVoice = item.type === 'voice_call' || item.type === 'call';
  const isCallback = item.type === 'voice_message';
  const isStrategyCall = isCallback && item.subtype === 'strategy_call';
  const isValuation = isCallback && item.subtype === 'valuation';
  const Icon = isValuation ? TrendingUp : isStrategyCall ? Sparkles : isCallback ? PhoneForwarded : isVoice ? Phone : MessageSquare;
  const opened = () => {
    if (item.unread) onOpen(item);
    if (isValuation && onValuationClick) onValuationClick(item);
  };
  const toggle = (e) => { e.stopPropagation(); setExpanded((v) => !v); };
  return (
    <div
      data-testid={`inbox-row-${item.id}`}
      className={`w-full text-left bg-white rounded-xl p-4 shadow-sm border-l-4 ${
        item.unread ? 'border-brand-gold' : 'border-transparent opacity-80'
      } hover:shadow-md transition-shadow`}
    >
      <button onClick={opened} className="w-full text-left flex items-start gap-3">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
          isValuation ? 'bg-yellow-100'
          : isStrategyCall ? 'bg-emerald-100'
          : 'bg-brand-cream'
        }`}>
          <Icon className={`w-4 h-4 ${
            isValuation ? 'text-yellow-800'
            : isStrategyCall ? 'text-emerald-700'
            : 'text-brand-green'
          }`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="font-semibold text-brand-green truncate flex items-center gap-2 flex-wrap">
              {item.contact || item.number || 'Unknown'}
              {isValuation && (
                <span data-testid={`inbox-valuation-chip-${item.id}`}
                      className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-semibold bg-yellow-100 text-yellow-900 uppercase tracking-wide">
                  <TrendingUp className="w-3 h-3" /> Valuation
                </span>
              )}
              {isStrategyCall && (
                <span data-testid={`inbox-strategy-chip-${item.id}`}
                      className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-semibold bg-emerald-100 text-emerald-800 uppercase tracking-wide">
                  <Sparkles className="w-3 h-3" /> Strategy call
                </span>
              )}
              {isCallback && !isStrategyCall && !isValuation && (
                <span data-testid={`inbox-callback-chip-${item.id}`}
                      className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-semibold bg-amber-100 text-amber-800 uppercase tracking-wide">
                  Callback
                </span>
              )}
            </div>
            <div className="text-xs text-brand-green/60 whitespace-nowrap">{fmt(item.at)}</div>
          </div>
          <div className="text-xs text-brand-green/60">
            {item.type === 'voice_call' ? 'AI call'
              : item.type === 'call' ? 'Call'
              : item.type === 'voice_message' ? (
                  isValuation ? 'AI valuation report'
                  : isStrategyCall ? '15-min strategy call'
                  : 'Callback request'
                )
              : 'SMS'}
            {' · '}{item.direction || 'inbound'}
            {item.number ? ` · ${item.number}` : ''}
            {item.duration_seconds != null ? ` · ${item.duration_seconds}s` : ''}
            {item.type === 'voice_call' && item.voice_persona && (
              <span
                data-testid={`inbox-voice-badge-${item.id}`}
                title={item.voice_label || item.voice_persona}
                className="ml-2 inline-flex items-center gap-1 rounded-full bg-brand-cream text-brand-green px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide align-middle border border-brand-green/10"
              >
                <Mic className="w-3 h-3 text-brand-gold" /> {item.voice_persona}
              </span>
            )}
            {item.redaction_flag?.detected && (
              <span
                data-testid={`inbox-redaction-badge-${item.id}`}
                title={`Contains Fair-Housing sensitive terms${
                  item.redaction_flag.terms?.length
                    ? ': ' + item.redaction_flag.terms.slice(0, 4).join(', ')
                    : ''
                }. Review before sharing.`}
                className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide align-middle"
              >
                <ShieldAlert className="w-3 h-3" /> Fair-Housing
              </span>
            )}
          </div>
          {item.type === 'voice_call' && item.summary && (
            <p className="text-sm text-brand-green mt-2 whitespace-pre-wrap" data-testid={`inbox-summary-${item.id}`}>
              {item.summary}
            </p>
          )}
          {item.type === 'call' && item.transcript && typeof item.transcript === 'string' && (
            <p className="text-sm text-brand-green/80 mt-2 line-clamp-3 whitespace-pre-wrap">
              {item.transcript}
            </p>
          )}
          {item.type === 'voice_message' && (item.messages || []).slice(-1).map((m, i) => (
            <p key={i} className="text-sm text-brand-green mt-2 whitespace-pre-wrap"
               data-testid={`inbox-callback-body-${item.id}`}>
              {m.body}
            </p>
          ))}
          {item.type === 'sms' && (item.messages || []).slice(-1).map((m, i) => (
            <p key={i} className="text-sm text-brand-green/80 mt-2 line-clamp-2">
              <span className="text-xs uppercase tracking-wide text-brand-green/50 mr-2">{m.from}</span>
              {m.text}
            </p>
          ))}
        </div>
        {item.unread && (
          <span className="text-[10px] uppercase tracking-wide text-brand-gold font-semibold">
            New
          </span>
        )}
      </button>

      {item.type === 'voice_call' && (
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          {item.recording_url && (
            <a href={item.recording_url} target="_blank" rel="noreferrer"
               data-testid={`inbox-play-${item.id}`}
               onClick={(e) => {
                 // Confirm before opening a recording that contains protected-class terms.
                 if (item.redaction_flag?.detected) {
                   const ok = window.confirm(
                     'This call contains Fair-Housing sensitive terms. ' +
                     'Play or share the recording carefully — protected-class ' +
                     'language can create liability. Continue?'
                   );
                   if (!ok) { e.preventDefault(); return; }
                 }
                 e.stopPropagation();
               }}
               className="inline-flex items-center gap-1 text-xs text-brand-green hover:underline">
              <Play className="w-3 h-3" /> Play recording
            </a>
          )}
          <button
            data-testid={`inbox-expand-${item.id}`}
            onClick={toggle}
            className="inline-flex items-center gap-1 text-xs text-brand-green/70 hover:text-brand-green"
          >
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {expanded ? 'Hide transcript' : 'Show transcript'}
          </button>
        </div>
      )}

      {expanded && item.type === 'voice_call' && (
        <div className="mt-3 border-t border-brand-cream pt-3 space-y-1 text-sm" data-testid={`inbox-transcript-${item.id}`}>
          {item.redaction_flag?.detected && (
            <div
              data-testid={`inbox-redaction-banner-${item.id}`}
              className="mb-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
            >
              <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="font-semibold text-amber-900">
                  Fair-Housing sensitive language detected
                </div>
                <p className="mt-1 text-amber-800/90">
                  This call transcript mentioned {item.redaction_flag.terms?.length || 0}{' '}
                  protected-class term{(item.redaction_flag.terms?.length || 0) === 1 ? '' : 's'}
                  {(item.redaction_flag.terms || []).length > 0 && (
                    <>
                      : <span className="italic">
                        {item.redaction_flag.terms.slice(0, 6).map((t) => `"${t}"`).join(', ')}
                      </span>
                    </>
                  )}
                  . Review before sharing this recording anywhere.
                </p>
              </div>
            </div>
          )}
          {(item.transcript || []).length > 0 && (
            <div className="mt-3 space-y-3" data-testid={`inbox-transcript-${item.id}`}>
              {(item.transcript || []).map((t, i) => {
                const isAI = t.role === 'assistant';
                const label = isAI
                  ? (item.voice_persona ? `AI — ${item.voice_persona}` : 'AI')
                  : (item.contact && item.contact !== 'AI-answered call' ? item.contact : 'Caller');
                return (
                  <div key={i} className={`flex ${isAI ? 'justify-start' : 'justify-end'}`}>
                    <div className={`max-w-[85%] ${isAI ? 'text-left' : 'text-right'}`}>
                      <div className={`text-[10px] uppercase tracking-wide font-semibold mb-1 ${
                        isAI ? 'text-brand-gold' : 'text-brand-green/60'
                      }`}>
                        {label}
                      </div>
                      <div
                        data-testid={`inbox-transcript-bubble-${item.id}-${i}`}
                        className={`inline-block rounded-2xl px-3 py-2 text-sm leading-relaxed text-left whitespace-pre-wrap ${
                          isAI
                            ? 'bg-brand-cream text-brand-green rounded-tl-sm'
                            : 'bg-brand-green text-brand-cream rounded-tr-sm'
                        }`}
                      >
                        {t.text}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {(item.tool_calls || []).map((tc, i) => (
            <div key={`tc-${i}`} className="text-xs bg-brand-cream/50 rounded p-2 mt-2 flex items-start gap-2">
              <Wrench className="w-3 h-3 mt-0.5 text-brand-green" />
              <div className="flex-1">
                <div className="font-medium text-brand-green">{tc.name}</div>
                <div className="text-brand-green/70">{JSON.stringify(tc.args || {})}</div>
                <div className="text-brand-green/60">→ {JSON.stringify(tc.result || {})}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CrmInbox() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all|unread|calls|sms
  const [valuationItem, setValuationItem] = useState(null);
  const { toast } = useToast();
  const { user } = useAuth();
  const { permission, requestPermission } = useInboxUnread({ user });

  const askForNotifications = useCallback(async () => {
    const p = await requestPermission();
    if (p === 'granted') {
      toast({ title: 'Notifications on', description: "We'll ping you when a new call or SMS arrives." });
    } else if (p === 'denied') {
      toast({ title: 'Blocked by browser', description: 'Enable notifications in your browser settings.', variant: 'destructive' });
    }
  }, [requestPermission, toast]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try { setItems(await listInteractions()); }
    catch (err) {
      toast({ title: 'Could not load inbox', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { refresh(); }, [refresh]);

  const filtered = useMemo(() => {
    if (filter === 'unread') return items.filter((i) => i.unread);
    if (filter === 'calls') return items.filter((i) => i.type === 'call' || i.type === 'voice_call');
    if (filter === 'sms') return items.filter((i) => i.type === 'sms');
    return items;
  }, [items, filter]);

  const open = useCallback(async (item) => {
    try {
      await markInteractionRead(item.id);
      setItems((prev) => prev.map((x) => x.id === item.id ? { ...x, unread: false } : x));
    } catch { /* ignore */ }
  }, []);

  const markAllRead = useCallback(async () => {
    const unread = items.filter((i) => i.unread);
    await Promise.all(unread.map((i) => markInteractionRead(i.id).catch(() => null)));
    setItems((prev) => prev.map((x) => ({ ...x, unread: false })));
    toast({ title: 'Marked all as read' });
  }, [items, toast]);

  const unreadCount = items.filter((i) => i.unread).length;

  return (
    <div data-testid="crm-inbox">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="font-serif text-2xl text-brand-green">
          Inbox {unreadCount > 0 && (
            <span className="ml-2 text-xs px-2 py-1 bg-brand-gold text-brand-green rounded-full align-middle">
              {unreadCount} new
            </span>
          )}
        </h2>
        <div className="flex items-center gap-2">
          {permission !== 'granted' && (
            <Button variant="outline" size="sm" onClick={askForNotifications}
                    data-testid="inbox-enable-notifications">
              {permission === 'denied' ? <><BellOff className="w-4 h-4 mr-1" /> Notifications blocked</>
                                       : <><Bell className="w-4 h-4 mr-1" /> Enable notifications</>}
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={refresh} data-testid="inbox-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" onClick={markAllRead} data-testid="inbox-mark-all-read">
              <MailOpen className="w-4 h-4 mr-1" /> Mark all read
            </Button>
          )}
        </div>
      </div>

      <Tabs value={filter} onValueChange={setFilter} className="mb-4">
        <TabsList data-testid="inbox-tabs">
          <TabsTrigger value="all" data-testid="inbox-tab-all">All</TabsTrigger>
          <TabsTrigger value="unread" data-testid="inbox-tab-unread">Unread</TabsTrigger>
          <TabsTrigger value="calls" data-testid="inbox-tab-calls">Calls</TabsTrigger>
          <TabsTrigger value="sms" data-testid="inbox-tab-sms">SMS</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <p className="text-brand-green/60">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl p-8 text-center">
          <p className="text-brand-green/70">Nothing here yet.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {filtered.map((it) => (
            <li key={it.id}>
              <InteractionCard item={it} onOpen={open}
                                onValuationClick={setValuationItem} />
            </li>
          ))}
        </ul>
      )}

      <ValuationDetailDialog
        item={valuationItem}
        open={!!valuationItem}
        onOpenChange={(v) => { if (!v) setValuationItem(null); }}
      />
    </div>
  );
}
